import {
  validateRelationshipInvestigationConfig,
  type RelationshipInvestigationConfig,
} from './RelationshipInvestigationConfig.js';
import {
  RelationshipInvestigationState,
  type RelationshipComparisonResult,
  type RelationshipInvestigationSnapshot,
} from './RelationshipInvestigationState.js';

export interface RelationshipInvestigationEvents {
  readonly onStateChanged?: (snapshot: RelationshipInvestigationSnapshot) => void;
  readonly onInteractionObserved?: (
    interactionId: string,
    snapshot: RelationshipInvestigationSnapshot,
  ) => void;
  readonly onEvidenceCollected?: (
    evidenceId: string,
    snapshot: RelationshipInvestigationSnapshot,
  ) => void;
  readonly onComparisonAttempt?: (
    result: RelationshipComparisonResult,
    snapshot: RelationshipInvestigationSnapshot,
  ) => void;
  readonly onCompleted?: (
    result: RelationshipComparisonResult,
    config: RelationshipInvestigationConfig,
    snapshot: RelationshipInvestigationSnapshot,
  ) => void;
}

/**
 * Rendering-independent relationship investigation. Production-art views can
 * rebuild for resize or resume from a persisted snapshot without losing the
 * child's observations, evidence, or prediction.
 */
export class RelationshipInvestigation {
  private readonly state: RelationshipInvestigationState;
  private completionEmitted = false;

  constructor(
    readonly config: RelationshipInvestigationConfig,
    private readonly events: RelationshipInvestigationEvents = {},
    initialSnapshot?: RelationshipInvestigationSnapshot,
  ) {
    validateRelationshipInvestigationConfig(config);
    this.state = new RelationshipInvestigationState(config, initialSnapshot);
    this.completionEmitted = this.state.isComplete;
  }

  getSnapshot() { return this.state.snapshot(); }

  acknowledgeObservation() { return this.change(() => this.state.acknowledgeObservation()); }
  choosePrediction(conditionId: string) {
    return this.change(() => this.state.choosePrediction(conditionId));
  }

  recordInteraction(interactionId: string) {
    const changed = this.state.recordInteraction(interactionId);
    if (!changed) return false;
    const snapshot = this.state.snapshot();
    this.events.onInteractionObserved?.(interactionId, snapshot);
    this.events.onStateChanged?.(snapshot);
    return true;
  }

  beginEvidenceCollection() {
    return this.change(() => this.state.beginEvidenceCollection());
  }

  collectEvidence(evidenceId: string) {
    const changed = this.state.collectEvidence(evidenceId);
    if (!changed) return false;
    const snapshot = this.state.snapshot();
    this.events.onEvidenceCollected?.(evidenceId, snapshot);
    this.events.onStateChanged?.(snapshot);
    return true;
  }

  compareEvidence(conditionId: string) {
    const result = this.state.compareEvidence(conditionId);
    if (result) {
      const snapshot = this.state.snapshot();
      this.events.onComparisonAttempt?.(result, snapshot);
      this.events.onStateChanged?.(snapshot);
    }
    return result;
  }

  applyFinding() {
    const changed = this.state.applyFinding();
    if (!changed) return false;
    const snapshot = this.state.snapshot();
    this.events.onStateChanged?.(snapshot);
    const result = snapshot.comparison;
    if (!this.completionEmitted && result) {
      this.completionEmitted = true;
      this.events.onCompleted?.(result, this.config, snapshot);
    }
    return true;
  }

  reset() {
    this.state.reset();
    this.completionEmitted = false;
    this.emitState();
  }

  private change(operation: () => boolean) {
    const changed = operation();
    if (changed) this.emitState();
    return changed;
  }

  private emitState() { this.events.onStateChanged?.(this.state.snapshot()); }
}
